import React from 'react';
import { Minus, Plus, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, total, itemCount } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">
            Carrello <span className="text-[#FF7043]">({itemCount})</span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <div className="w-20 h-20 rounded-full bg-[#FFF8F5] flex items-center justify-center mb-4">
              <span className="text-4xl">🛒</span>
            </div>
            <p className="font-heading text-lg font-semibold text-slate-800 mb-1">Il carrello è vuoto</p>
            <p className="text-slate-500 text-sm mb-6">Scopri i nostri prodotti esclusivi!</p>
            <Link
              to="/shop"
              onClick={() => setIsOpen(false)}
              data-testid="cart-goto-shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
            >
              Vai allo Shop <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              {items.map((item, idx) => (
                <div
                  key={`${item.product_id}-${item.size}-${idx}`}
                  data-testid={`cart-item-${item.product_id}`}
                  className="flex gap-3 p-3 rounded-2xl bg-slate-50 group"
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-xl shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-heading font-semibold text-sm text-slate-800 truncate">{item.name}</p>
                        {item.size && (
                          <span className="text-xs text-slate-500">Taglia: {item.size}</span>
                        )}
                      </div>
                      <button
                        data-testid={`cart-remove-${item.product_id}`}
                        onClick={() => removeItem(item.product_id, item.size)}
                        className="p-1 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200">
                        <button
                          data-testid={`cart-decrease-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                          className="p-1.5 hover:bg-slate-50 rounded-full transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                        <button
                          data-testid={`cart-increase-${item.product_id}`}
                          onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                          className="p-1.5 hover:bg-slate-50 rounded-full transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <p className="font-heading font-bold text-[#FF7043]">
                        €{(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg font-semibold text-slate-800">Totale</span>
                <span className="font-heading text-2xl font-bold text-[#FF7043]">€{total.toFixed(2)}</span>
              </div>
              <Link
                to="/checkout"
                onClick={() => setIsOpen(false)}
                data-testid="cart-checkout-btn"
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#FF7043] text-white font-heading font-bold text-lg rounded-full btn-caribbean"
              >
                Vai al Checkout <ArrowRight size={18} />
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
